import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import Head from 'next/head';
import Layout from '@/layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { canonicalUrl } from '@/shared/utils';
import { page3JsonLd } from '@/lib/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';

const ReportContent = dynamic(() => import('@/components/Report'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl, params, query } = context;
  const { id } = params;
  const { name = '', avatar = '' } = query;

  const localeRes = await serverSideTranslations(locale, ['common']);

  const parentPath = removeLastPathSegment(resolvedUrl);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;
  const parentUrl = siteUrl + parentPath;

  const jsonLd = page3JsonLd(
    {
      title: hyphenatedToTitleCase(`${name}`),
      desc: tempData?.head_Meta_Desc ?? null,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: 'Instagram Tracking Example',
      url: parentUrl,
    },
    {
      url: siteUrl,
    }
  );
  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, null, null),
      id,
      name,
      avatar,
      title: tempData['head_Meta_Title'],
      description: tempData['head_Meta_Desc'],
      jsonLd,
    },
  };
};

const IndexPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <ReportContent data={props} />;
};

IndexPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const jsonLd = page?.props?.jsonLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default IndexPage;
